import React, { useContext, useRef, useEffect, useState } from "react";
import { Box, Typography } from "@material-ui/core";
import { useHistory } from "react-router";
import clsx from "clsx";
import {
  B2BSpinner,
  ButtonWrap,
  CheckoutStepper,
  Icon,
  IconName,
  LoadingIndicator,
  Tooltip,
} from "halifax";
import {
  getParentState,
  ParentState,
  useCheckoutState,
  useCheckoutStateSelector as useSelector,
} from "@capone/checkout";
import { getConfigTenant, isVesperTenant } from "@capone/common";

import "./styles.scss";
import { PATH_BOOK_CONFIRMATION, PATH_HOME } from "../../../../utils/paths";
import { ClientContext } from "../../../../App";
import {
  TRAVELERS_HEADING,
  HEADER_MAIN_TEXT,
  HEADER_SUB_TEXT,
  LOADING_TEXT,
  PAYMENT_HEADING,
  PAYMENT_SUBHEADING,
  PRIMARY_TRAVELER_HEADING,
  ADDITIONAL_DETAILS_HEADING,
  PAYMENT_EARN_INFO_TOOLTIP_TEXT,
} from "./textConstants";
import {
  CHANGE_CTA_TEXT,
  CONTACT_INFORMATION,
} from "../../../common/textConstants";
import {
  PriceBreakdown,
  TravelerSelection,
  TreesCard,
  ContactInfo,
  ActivityRewardsAndPayment,
  PrimaryTravelerSelection,
  ActivitySummaryPanel,
  AdditionalDetails,
} from "..";
import { Event, TEvent } from "../../state/events";
import { ActivityMachineContext } from "../../state/types";
import { getCheckoutSteps } from "./utils";
import {
  ActivityAdditionalDetailsSelectors,
  ActivityTravelerSelectors,
} from "../../../../checkout";
import { ShopContactInformation } from "../../../common";
import { ActivitySelectors } from "../../state/selectors";
import {
  AVAILABLE,
  getExperimentVariant,
  isCheckoutImprovementsEnabled,
  CHECKOUT_DEFAULT_COMM_EXPERIMENT,
  TREES_MODAL_EXPERIMENT,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../../../context/experiments";
import { AdditionalDetailsCompleted } from "../AdditionalDetails";

export type DesktopActivityBookValidationError =
  | "travelers"
  | "contact-info"
  | "primary-traveler"
  | "rewards"
  | "card";

export const DesktopActivityBookWorkflow = () => {
  const clientContext = useContext(ClientContext);
  const history = useHistory();
  const [validationErrorTypes, setValidationErrorTypes] =
    useState<DesktopActivityBookValidationError[]>();
  const travelerInfoSectionRef = useRef<HTMLDivElement>(null);
  const travelerPrimarySectionRef = useRef<HTMLDivElement>(null);
  const additionalDetailsSectionRef = useRef<HTMLDivElement>(null);
  const paymentSectionRef = useRef<HTMLDivElement>(null);

  const [state, send] = useCheckoutState<TEvent, ActivityMachineContext>();

  const parentState = getParentState(state.value) as ParentState;

  const activityShop = useSelector(ActivitySelectors.getActivityShop);
  const selectedTravelers = useSelector(
    ActivityTravelerSelectors.getAllSelectedUserTravelers,
  );
  const selectedTravelerIds = useSelector(
    ActivityTravelerSelectors.getSelectedTravelerIds,
  );
  const expState = useExperiments();
  const isCheckoutImprovementsAvailable = isCheckoutImprovementsEnabled(
    expState.experiments,
  );
  const isSinglePrimaryTraveler =
    isCheckoutImprovementsAvailable && selectedTravelerIds.length === 1;
  const selectedPrimaryTraveler = useSelector(
    ActivityTravelerSelectors.getSelectedPrimaryTraveler,
  );
  const hasAdditionalDetailsStep = useSelector(
    ActivityAdditionalDetailsSelectors.getHasAdditionalDetailsStep,
  );
  const hasCompletedAdditionalDetailsStep = useSelector(
    ActivityAdditionalDetailsSelectors.getHasCompletedAdditionalDetailsStep,
  );

  const treesModalExperiment = getExperimentVariant(
    expState.experiments,
    TREES_MODAL_EXPERIMENT,
  );
  const isTreesExperiment = treesModalExperiment === AVAILABLE;

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const isDefaultCommEnabled =
    getExperimentVariant(expState.experiments, CHECKOUT_DEFAULT_COMM_EXPERIMENT) ===
    AVAILABLE;

  const isVesper = isVesperTenant(getConfigTenant());

  const goToTravelers = () => {
    send(Event.GO_TO_TRAVELER_SELECT);
  };

  const goToPrimaryTraveler = () => {
    send(Event.GO_TO_PRIMARY_TRAVELER_SELECT);
  };

  const goToAdditionalDetails = () => {
    send(Event.GO_TO_ADDITIONAL_DETAILS);
  };

  useEffect(() => {
    switch (parentState) {
      case ParentState.activityTravelerInformation:
        travelerInfoSectionRef.current?.scrollIntoView({ behavior: "smooth" });
        break;
      case ParentState.activityPrimaryTraveler:
        travelerPrimarySectionRef.current?.scrollIntoView({
          behavior: "smooth",
        });
        break;
      case ParentState.activityAdditionalDetails:
        additionalDetailsSectionRef.current?.scrollIntoView({
          behavior: "smooth",
        });
        break;
      case ParentState.cardPayment:
        paymentSectionRef.current?.scrollIntoView({ behavior: "smooth" });
        break;
      default:
        break;
    }
  }, [parentState]);

  useEffect(() => {
    // update path so header becomes visible for confirmation
    if (parentState === ParentState.bookingConfirmation) {
      history.replace(PATH_BOOK_CONFIRMATION);
    }
  }, [parentState]);

  useEffect(() => {
    send({ type: Event.SET_PLATFORM, platform: "desktop" });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box
      className={clsx("desktop-activity-book-workflow-root", {
        xmod: isXmodRedesign,
        redesigned: isVesper,
      })}
    >
      <CheckoutStepper
        steps={getCheckoutSteps(parentState)}
        headerCopy={HEADER_MAIN_TEXT}
        subHeaderCopy={HEADER_SUB_TEXT}
        logo={
          <ButtonWrap
            className="logo"
            onClick={() => {
              history.push(PATH_HOME);
            }}
          >
            {clientContext.logo}
          </ButtonWrap>
        }
        className="b2b combined-step"
      />

      <Box className="activity-book-body-root">
        <Box className="activity-book-body-wrapper">
          <Box className="activity-book-body-left">
            <ActivitySummaryPanel displayChangeBtn />

            <div
              className={clsx("activity-book-section", {
                selected: [
                  ParentState.activityTravelerInformation,
                  ParentState.passport,
                  ParentState.contactInformation,
                ].includes(parentState),
              })}
              ref={travelerInfoSectionRef}
            >
              <Box className="activity-book-section-main-content-wrapper">
                <Typography
                  variant="h3"
                  className="activity-book-section-heading"
                >
                  {TRAVELERS_HEADING}
                </Typography>
                
                {parentState === ParentState.loading && (
                  <LoadingIndicator
                    className="activity-checkout-loading-indicator"
                    indicatorSize="small"
                    indicator={B2BSpinner}
                    message={LOADING_TEXT}
                  />
                )}

                {[
                  ParentState.activityTravelerInformation,
                  ParentState.passport,
                  ParentState.contactInformation,
                ].includes(parentState) ? (
                  <>
                    {!isDefaultCommEnabled && <hr />}

                    <TravelerSelection
                      validationErrorTypes={validationErrorTypes}
                    />

                    {!isDefaultCommEnabled && <hr />}

                    <ContactInfo
                      validationErrorTypes={validationErrorTypes}
                      setValidationErrorTypes={setValidationErrorTypes}
                    />
                  </>
                ) : (
                  !!selectedTravelers.length && (
                    <Box className="activity-book-filled-section">
                      <Icon name={IconName.User} />
                      <Typography className="activity-book-filled-section-content">
                        {selectedTravelers.reduce((nameString, person, i) => {
                          const firstLastName = `${person.givenName} ${person.surname}`;
                          if (i > 0) return `${nameString}, ${firstLastName}`;

                          return firstLastName;
                        }, "")}
                      </Typography>
                    </Box>
                  )
                )}
              </Box>
              {![
                ParentState.activityTravelerInformation,
                ParentState.passport,
                ParentState.contactInformation,
              ].includes(parentState) &&
                !!selectedTravelers.length && (
                  <Box className="activity-book-section-change-cta-wrapper">
                    <ButtonWrap onClick={goToTravelers}>
                      <Typography>{CHANGE_CTA_TEXT}</Typography>
                    </ButtonWrap>
                  </Box>
                )}
            </div>

            <div
              className={clsx("activity-book-section", {
                selected:
                  !isSinglePrimaryTraveler &&
                  [ParentState.activityPrimaryTraveler].includes(parentState),
              })}
              ref={travelerPrimarySectionRef}
            >
              <Box className="activity-book-section-main-content-wrapper">
                <Typography
                  variant="h3"
                  className="activity-book-section-heading"
                >
                  {PRIMARY_TRAVELER_HEADING}
                </Typography>

                {!isSinglePrimaryTraveler &&
                [ParentState.activityPrimaryTraveler].includes(parentState) ? (
                  <PrimaryTravelerSelection
                    validationErrorTypes={validationErrorTypes}
                    setValidationErrorTypes={setValidationErrorTypes}
                  />
                ) : (
                  selectedPrimaryTraveler && (
                    <Box className="activity-book-filled-section">
                      <Icon name={IconName.User} />
                      <Typography className="activity-book-filled-section-content">
                        {selectedPrimaryTraveler.givenName}{" "}
                        {selectedPrimaryTraveler.surname}
                      </Typography>
                    </Box>
                  )
                )}
              </Box>
              {!isSinglePrimaryTraveler &&
                ![ParentState.activityPrimaryTraveler].includes(parentState) &&
                selectedPrimaryTraveler && (
                  <Box className="activity-book-section-change-cta-wrapper">
                    <ButtonWrap onClick={goToPrimaryTraveler}>
                      <Typography>{CHANGE_CTA_TEXT}</Typography>
                    </ButtonWrap>
                  </Box>
                )}
            </div>

            {hasAdditionalDetailsStep && (
              <div
                className={clsx("activity-book-section", {
                  selected: [ParentState.activityAdditionalDetails].includes(
                    parentState,
                  ),
                })}
                ref={additionalDetailsSectionRef}
              >
                <Box className="activity-book-section-main-content-wrapper">
                  <Typography
                    variant="h3"
                    className="activity-book-section-heading"
                  >
                    {ADDITIONAL_DETAILS_HEADING}
                  </Typography>

                  {[ParentState.activityAdditionalDetails].includes(
                    parentState,
                  ) ? (
                    <AdditionalDetails />
                  ) : (
                    hasCompletedAdditionalDetailsStep && (
                      <Box className="activity-book-filled-section">
                        <Typography className="activity-book-filled-section-content">
                          <AdditionalDetailsCompleted />
                        </Typography>
                      </Box>
                    )
                  )}
                </Box>
                {![ParentState.activityAdditionalDetails].includes(
                  parentState,
                ) &&
                  hasCompletedAdditionalDetailsStep && (
                    <Box className="activity-book-section-change-cta-wrapper">
                      <ButtonWrap onClick={goToAdditionalDetails}>
                        <Typography>{CHANGE_CTA_TEXT}</Typography>
                      </ButtonWrap>
                    </Box>
                  )}
              </div>
            )}

            <div
              className={clsx("activity-book-section", {
                selected: [
                  ParentState.wallet,
                  ParentState.rewardsPayment,
                  ParentState.cardPayment,
                ].includes(parentState),
              })}
              ref={paymentSectionRef}
            >
              <Box className="activity-book-section-main-content-wrapper">
                <Typography
                  variant="h3"
                  className="activity-book-section-heading"
                >
                  {PAYMENT_HEADING(hasAdditionalDetailsStep)}
                </Typography>

                {parentState === ParentState.cartUpdate && (
                  <LoadingIndicator
                    className="activity-checkout-loading-indicator"
                    indicatorSize="small"
                    indicator={B2BSpinner}
                    message={LOADING_TEXT}
                  />
                )}

                {[ParentState.cardPayment].includes(parentState) && (
                  <>
                    <Box className="activity-book-section-subheading-wrapper">
                      <Typography
                        variant="h4"
                        className="activity-book-section-subheading"
                      >
                        {PAYMENT_SUBHEADING}
                      </Typography>
                      <Tooltip
                        content={
                          <Typography className="payment-earn-info-popover-content">
                            {PAYMENT_EARN_INFO_TOOLTIP_TEXT}
                          </Typography>
                        }
                        triggerClassName="payment-earn-info-btn"
                      >
                        <Icon name={IconName.InfoCircle} />
                      </Tooltip>
                    </Box>
                    <hr />
                    <ActivityRewardsAndPayment
                      validationErrorTypes={validationErrorTypes}
                      setValidationErrorTypes={setValidationErrorTypes}
                    />
                  </>
                )}
              </Box>
            </div>

            <div className="activity-book-section contact-info-section">
              <Typography className="contact-info-title" variant="h3">
                {CONTACT_INFORMATION}
              </Typography>
              <ShopContactInformation
                supplierEmail={activityShop?.supplierInfo.email}
                supplierPhone={activityShop?.supplierInfo.phone}
              />
            </div>
            {isTreesExperiment && <TreesCard />}
          </Box>
          <Box className="activity-book-body-right">
            <Box className="activity-book-body-right-sticky-container">
              <PriceBreakdown
                setValidationErrorTypes={setValidationErrorTypes}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
